import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnDestroy, OnInit, ViewEncapsulation, computed, effect, input, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { concat } from 'rxjs';
import { EventBusService, UserApiService } from 'shared/services';
import { isCurrentSupplierUser } from 'shared/interfaces/current-supplier-user.interface';
import {
    DerivedVisualPermissionUpdateInputGraph,
    VisualPermission,
    VisualPermissionCreateInputGraph,
    VisualPermissionResponse,
    VisualPermissionUpdateInputGraph,
    VisualPermissionsAccessTypeEnum,
    VisualPermissionsEnum,
    visualPermissionsSetup,
} from 'users/interfaces/visual-permissions.interface';
import { VisualsPermissionsService } from 'visuals/services/visuals-permissions.service';

interface ProcessedPermission extends VisualPermission {
    isVisible: boolean;
    isImplemented: boolean;
    translationKey: string;
    children: ProcessedPermission[];
}

interface BackendPermissionPayload {
    type: VisualPermissionsEnum;
    accessType: VisualPermissionsAccessTypeEnum;
    derivedVisualPermissions?: {
        type: VisualPermissionsEnum;
        isActive: boolean;
    }[];
}

@Component({
    selector: 'app-supplier-user-edit-visuals-permissions',
    imports: [TranslateModule, ReactiveFormsModule, CommonModule],
    templateUrl: './supplier-user-edit-visuals-permissions.component.html',
    styleUrl: './supplier-user-edit-visuals-permissions.component.scss',
    encapsulation: ViewEncapsulation.None,
    host: {
        style: 'display: contents;',
    },
})
export class SupplierUserEditVisualsPermissionsComponent implements OnInit, OnDestroy {
    private accessTypeHierarchy: VisualPermissionsAccessTypeEnum[] = this.visualsPermissionsService.accessTypeHierarchy;

    permissionData = input.required<VisualPermissionResponse[]>();
    userId = input<string | null>(null);
    groupId = input<string | null>(null);
    formGenerated = signal<boolean>(false);

    permissionsToLoad = computed(() => {
        return this.permissionData()?.length === 0 ? this.visualsPermissionsService.defaultPermissions() : this.permissionData();
    });

    permissionsSetup = computed<VisualPermission[]>(() => {
        const defaultPermissions = this.visualsPermissionsService.defaultPermissions();
        return defaultPermissions.map((defaultPermission) => {
            const localPermissionData = visualPermissionsSetup.find(
                (visualPermissionSetup) => visualPermissionSetup.name === defaultPermission.type,
            );
            const visualPermission: VisualPermission = {
                name: defaultPermission.type,
                isImplemented: defaultPermission.isImplemented,
                possibleAccessTypes: localPermissionData ? localPermissionData.possibleAccessTypes : [],
                dependsOnAccessType: localPermissionData ? localPermissionData.dependsOnAccessType : undefined,
                children: defaultPermission.derivedVisualPermissions
                    ? defaultPermission.derivedVisualPermissions.map((derivedPermission) => {
                          const localDerivedPermissionData = visualPermissionsSetup.find(
                              (visualPermissionSetup) => visualPermissionSetup.name === derivedPermission.type,
                          );
                          const derivedVisualPermission: VisualPermission = {
                              name: derivedPermission.type,
                              isImplemented: derivedPermission.isImplemented,
                              possibleAccessTypes: localDerivedPermissionData ? localDerivedPermissionData.possibleAccessTypes : [],
                              dependsOnAccessType: localDerivedPermissionData ? localDerivedPermissionData.dependsOnAccessType : undefined,
                          };
                          return derivedVisualPermission;
                      })
                    : [],
            };
            return visualPermission;
        });
    });

    isCreatePermissionsFlow = computed(() => {
        return this.permissionData()?.length === 0;
    });

    private originalFormStateSignal = signal<Record<string, boolean>>({});
    private formInitialized = signal<boolean>(false);

    permissionsForm = new FormGroup<{ [key: string]: FormControl<boolean> }>({});
    accessTypeEnum = VisualPermissionsAccessTypeEnum;

    hasActivePermissions = computed(() => {
        const flattenPermissions = (permissions: ProcessedPermission[]): ProcessedPermission[] => {
            const result: ProcessedPermission[] = [];
            permissions.forEach((permission) => {
                if (permission.isVisible) {
                    result.push(permission);
                    if (permission.children && permission.children.length > 0) {
                        result.push(...flattenPermissions(permission.children));
                    }
                }
            });
            return result;
        };

        const allVisiblePermissions = flattenPermissions(this.processedPermissions());
        return allVisiblePermissions.some((permission) => permission.possibleAccessTypes.includes(this.accessTypeEnum.ACTIVE));
    });

    constructor(
        private visualsPermissionsService: VisualsPermissionsService,
        private eventBusService: EventBusService,
        private destroyRef: DestroyRef,
        private userApiService: UserApiService,
    ) {
        effect(() => {
            const permissionsSetup = this.permissionsSetup();
            const permissionsToLoad = this.permissionsToLoad();

            if (permissionsSetup.length === 0) return;

            this.generatePermissionsForm(permissionsSetup);
            this.populateFormFromPermissions(permissionsToLoad);
            this.formGenerated.set(true);
        });

        effect(() => {
            const createVisualPermissionsUserGroupId = this.visualsPermissionsService.createUserGroupVisualPermissionsId();
            if (createVisualPermissionsUserGroupId !== null) {
                const payload = this.prepareCreatePayload(true, createVisualPermissionsUserGroupId);
                this.createGroupPermissions(payload);
                this.visualsPermissionsService.createUserGroupVisualPermissionsId.set(null);
            }
        });

        effect(() => {
            const userId = this.userId();
            const groupId = this.groupId();
            const isOnCreatePermissionsFlow = this.isCreatePermissionsFlow();
            this.visualsPermissionsService.userId.set(userId);
            this.visualsPermissionsService.userGroupId.set(groupId);
            this.visualsPermissionsService.loadDefaultPermissions.set(true);
            this.visualsPermissionsService.isOnCreatePermissionsFlow.set(isOnCreatePermissionsFlow);
        });
    }

    private formValuesSignal = toSignal(this.permissionsForm.valueChanges, {
        initialValue: this.permissionsForm.value,
    });

    private processedFormValues = computed(() => {
        const formValues = this.formValuesSignal();

        const booleanValues: Record<string, boolean> = {};
        Object.entries(formValues || {}).forEach(([key, value]) => {
            booleanValues[key] = Boolean(value);
        });
        return booleanValues;
    });

    private implementedPermissionsMap = computed(() => {
        const permissionsToLoad = this.visualsPermissionsService.defaultPermissions();
        const implementedMap = new Map<VisualPermissionsEnum, boolean>();

        permissionsToLoad.forEach((permission) => {
            implementedMap.set(permission.type, permission.isImplemented);

            permission.derivedVisualPermissions?.forEach((derivedPermission) => {
                implementedMap.set(derivedPermission.type, derivedPermission.isImplemented);
            });
        });

        return implementedMap;
    });

    processedPermissions = computed<ProcessedPermission[]>(() => {
        const currentFormValues = this.processedFormValues();
        const implementedMap = this.implementedPermissionsMap();
        if (implementedMap.size === 0) {
            return [];
        }

        return this.permissionsSetup()
            .map((permission) => this.processPermission(permission, currentFormValues, implementedMap))
            .filter((permission) => permission.isImplemented);
    });

    ngOnInit(): void {
        this.visualsPermissionsService.visualsSectionActive.set(true);
        this.setupFormValidation();
        this.eventBusService
            .getObservableForEventStream('updateSupplierUserVisualPermissions')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.updateVisualPermissions());
        this.eventBusService
            .getObservableForEventStream('closeUserGroupModal')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((data: any) => {
                if (data?.operationType === 'update') {
                    this.updateVisualPermissions(true);
                }
            });
    }

    private populateFormFromPermissions(permissionsToLoad: VisualPermissionResponse[]): void {
        const grantableTypes = this.isCreatePermissionsFlow() ? this.getActingUserGrantableVisualTypes() : null;

        permissionsToLoad.forEach((permission) => {
            if (grantableTypes && !grantableTypes.has(permission.type)) {
                return;
            }
            if (
                permission.accessType === VisualPermissionsAccessTypeEnum.VIEW ||
                permission.accessType === VisualPermissionsAccessTypeEnum.EDIT
            ) {
                const control = this.permissionsForm.get(`${permission.type}_${permission.accessType}`);
                if (control) {
                    control.setValue(true, { emitEvent: false });
                }
            }
            if (permission.accessType === VisualPermissionsAccessTypeEnum.HIDE) {
                const viewControl = this.permissionsForm.get(`${permission.type}_${VisualPermissionsAccessTypeEnum.VIEW}`);
                const editControl = this.permissionsForm.get(`${permission.type}_${VisualPermissionsAccessTypeEnum.EDIT}`);
                if (viewControl) {
                    viewControl.setValue(false, { emitEvent: false });
                }
                if (editControl) {
                    editControl.setValue(false, { emitEvent: false });
                }
            }
            if (permission?.derivedVisualPermissions && permission.derivedVisualPermissions.length > 0) {
                permission.derivedVisualPermissions?.forEach((derivedPermission) => {
                    const derivedControlName = `${derivedPermission.type}_${VisualPermissionsAccessTypeEnum.ACTIVE}`;
                    const derivedControl = this.permissionsForm.get(derivedControlName);
                    if (derivedControl) {
                        derivedControl.setValue(derivedPermission.isActive, { emitEvent: false });
                    }
                });
            }
        });

        const currentFormValues: Record<string, boolean> = {};
        Object.entries(this.permissionsForm.value || {}).forEach(([key, value]) => {
            currentFormValues[key] = Boolean(value);
        });

        this.handleHierarchicalValidation(currentFormValues);
        this.handleParentChildValidation(currentFormValues);
        this.permissionsForm.updateValueAndValidity();

        const finalFormValues: Record<string, boolean> = {};
        Object.entries(this.permissionsForm.value || {}).forEach(([key, value]) => {
            finalFormValues[key] = Boolean(value);
        });
        this.originalFormStateSignal.set({ ...finalFormValues });
        this.formInitialized.set(true);
        this.publishNotAllowedChanges();
    }

    private updateVisualPermissions(isGroup: boolean = false): void {
        const payload = this.preparePayload(isGroup);
        if (payload.length > 0) {
            if (isGroup) {
                const groupId = this.groupId();
                if (this.isCreatePermissionsFlow() && groupId) {
                    const createPayload = this.prepareCreatePayload(true, groupId);
                    this.createGroupPermissions(createPayload);
                } else {
                    this.savePermissions(payload, true);
                }
            } else {
                const userId = this.userId();
                if (this.isCreatePermissionsFlow() && userId) {
                    const createPayload = this.prepareCreatePayload(false, userId);
                    this.createUserPermissions(createPayload);
                } else {
                    this.savePermissions(payload, false);
                }
            }
        } else {
            this.finishSave(true);
        }
    }

    /**
     * Releases the save flows waiting on the visual permissions call. On failure the modal is left open,
     * exactly as before, so the user can retry.
     */
    private finishSave(succeeded: boolean): void {
        if (succeeded) {
            this.visualsPermissionsService.closeSupplierUsersModal.set(true);
        }

        this.visualsPermissionsService.saveCompleted.next(succeeded);
    }

    /**
     * The visual permission types the acting user can grant. Returns null for Shiji and system users,
     * who are not capped by their own permissions.
     */
    private getActingUserGrantableVisualTypes(): Set<VisualPermissionsEnum> | null {
        const currentUser = this.userApiService.currentUser;

        if (!currentUser || !isCurrentSupplierUser(currentUser)) {
            return null;
        }

        return new Set(
            (currentUser.visualPermissions ?? [])
                .filter(
                    (permission) =>
                        permission.accessType === VisualPermissionsAccessTypeEnum.VIEW ||
                        permission.accessType === VisualPermissionsAccessTypeEnum.EDIT,
                )
                .map((permission) => permission.type),
        );
    }

    /**
     * Types the target has no stored permission for must be created, not updated - their id comes from the
     * in-memory default template and does not exist in the database.
     */
    private savePermissions(payload: VisualPermissionUpdateInputGraph[], isGroup: boolean): void {
        const storedTypes = new Set((this.permissionData() ?? []).map((permission) => permission.type));
        const toUpdate = payload.filter((permission) => storedTypes.has(permission.type));
        const toCreate: VisualPermissionCreateInputGraph[] = payload
            .filter((permission) => !storedTypes.has(permission.type))
            .map(({ id, ...rest }) => rest);

        if (!toCreate.length) {
            isGroup ? this.updateGroupPermissions(toUpdate) : this.updateUserPermissions(toUpdate);
            return;
        }

        const calls = [
            isGroup
                ? this.visualsPermissionsService.createGroupPermissions(toCreate)
                : this.visualsPermissionsService.createUserPermissions(toCreate),
        ];

        if (toUpdate.length) {
            calls.push(
                isGroup
                    ? this.visualsPermissionsService.updateGroupPermissions(toUpdate)
                    : this.visualsPermissionsService.updateUserPermissions(toUpdate),
            );
        }

        concat(...calls).subscribe({
            complete: () => this.finishSave(true),
            error: () => this.finishSave(false),
        });
    }

    private updateUserPermissions(payload: VisualPermissionUpdateInputGraph[]): void {
        this.visualsPermissionsService.updateUserPermissions(payload).subscribe({
            next: () => this.finishSave(true),
            error: () => this.finishSave(false),
        });
    }

    private updateGroupPermissions(payload: VisualPermissionUpdateInputGraph[]): void {
        this.visualsPermissionsService.updateGroupPermissions(payload).subscribe({
            next: () => this.finishSave(true),
            error: () => this.finishSave(false),
        });
    }

    private createGroupPermissions(payload: VisualPermissionCreateInputGraph[]): void {
        this.visualsPermissionsService.createGroupPermissions(payload).subscribe({
            next: () => this.finishSave(true),
            error: () => this.finishSave(false),
        });
    }

    private createUserPermissions(payload: VisualPermissionCreateInputGraph[]): void {
        this.visualsPermissionsService.createUserPermissions(payload).subscribe({
            next: () => this.finishSave(true),
            error: () => this.finishSave(false),
        });
    }

    private preparePayload(isGroup: boolean = false): VisualPermissionUpdateInputGraph[] {
        const changedPermissions = this.getChangedPermissions();
        const defaultPermissions = this.visualsPermissionsService.defaultPermissions();

        const payload: VisualPermissionUpdateInputGraph[] = changedPermissions.map((permission) => {
            const response = this.permissionsToLoad().find((p) => p.type === permission.type);
            const defaultPermission = defaultPermissions.find((p) => p.type === permission.type);

            const changedDerivedPermissions = permission.derivedVisualPermissions;
            const updatedDerivedVisualPermissions: DerivedVisualPermissionUpdateInputGraph[] = [];

            if (changedDerivedPermissions && changedDerivedPermissions.length > 0) {
                changedDerivedPermissions.forEach((derived) => {
                    const derivedResponse = response?.derivedVisualPermissions?.find((d) => d.type === derived.type);
                    const defaultDerivedPermission = defaultPermission?.derivedVisualPermissions?.find((d) => d.type === derived.type);

                    updatedDerivedVisualPermissions.push({
                        id: derivedResponse?.id || defaultDerivedPermission?.id || '',
                        isActive: derived.isActive,
                        type: derived.type,
                    });
                });
            }

            const returnValue: VisualPermissionUpdateInputGraph = {
                id: response?.id || defaultPermission?.id || '',
                accessType: permission.accessType,
                type: permission.type,
                updatedDerivedVisualPermissions: updatedDerivedVisualPermissions,
            };

            if (isGroup) {
                returnValue.userGroupId = response?.userGroupId || '';
            } else {
                returnValue.userId = response?.userId ?? this.userId() ?? '';
            }

            return returnValue;
        });

        return payload;
    }

    private prepareCreatePayload(isGroup: boolean = false, savedEntityId: string): VisualPermissionCreateInputGraph[] {
        const allPermissions = this.permissionsSetup();
        const currentFormValues: Record<string, boolean> = {};
        Object.entries(this.permissionsForm.value || {}).forEach(([key, value]) => {
            currentFormValues[key] = Boolean(value);
        });

        const payload: VisualPermissionCreateInputGraph[] = allPermissions
            .filter((permission) => permission.isImplemented)
            .map((permissionSetup) => {
                const viewChecked = currentFormValues[`${permissionSetup.name}_${VisualPermissionsAccessTypeEnum.VIEW}`] || false;
                const editChecked = currentFormValues[`${permissionSetup.name}_${VisualPermissionsAccessTypeEnum.EDIT}`] || false;

                let finalAccessType: VisualPermissionsAccessTypeEnum;
                switch (true) {
                    case editChecked:
                        finalAccessType = VisualPermissionsAccessTypeEnum.EDIT;
                        break;
                    case viewChecked:
                        finalAccessType = VisualPermissionsAccessTypeEnum.VIEW;
                        break;
                    default:
                        finalAccessType = VisualPermissionsAccessTypeEnum.HIDE;
                }

                const createdDerivedVisualPermissions: DerivedVisualPermissionUpdateInputGraph[] = [];

                if (permissionSetup.children && permissionSetup.children.length > 0) {
                    permissionSetup.children.forEach((derivedPermission) => {
                        const controlName = `${derivedPermission.name}_${VisualPermissionsAccessTypeEnum.ACTIVE}`;
                        const control = this.permissionsForm.get(controlName);
                        createdDerivedVisualPermissions.push({
                            id: '',
                            isActive: control?.value || false,
                            type: derivedPermission.name,
                        });
                    });
                }

                const returnValue: VisualPermissionCreateInputGraph = {
                    accessType: finalAccessType,
                    type: permissionSetup.name,
                    updatedDerivedVisualPermissions: createdDerivedVisualPermissions,
                };

                if (isGroup) {
                    returnValue.userGroupId = savedEntityId;
                } else {
                    returnValue.userId = savedEntityId;
                }

                return returnValue;
            });

        return payload;
    }

    private getChangedPermissions(): BackendPermissionPayload[] {
        if (!this.formInitialized()) {
            return [];
        }

        const currentFormValues: Record<string, boolean> = {};
        Object.entries(this.permissionsForm.value || {}).forEach(([key, value]) => {
            currentFormValues[key] = Boolean(value);
        });

        const originalFormState = this.originalFormStateSignal();

        const changedPermissions: BackendPermissionPayload[] = [];
        const processedParentTypes = new Set<VisualPermissionsEnum>();

        const changedControls = Object.keys(currentFormValues).filter((controlName) => {
            const currentValue = currentFormValues[controlName];
            const originalValue = originalFormState[controlName] ?? false;
            return currentValue !== originalValue;
        });

        if (changedControls.length === 0) {
            return [];
        }

        changedControls.forEach((controlName) => {
            const lastUnderscoreIndex = controlName.lastIndexOf('_');
            if (lastUnderscoreIndex === -1) return;

            const permissionType = controlName.substring(0, lastUnderscoreIndex) as VisualPermissionsEnum;
            const accessType = controlName.substring(lastUnderscoreIndex + 1) as VisualPermissionsAccessTypeEnum;

            const isParentPermission = this.isParentPermission(permissionType);

            if (isParentPermission && !processedParentTypes.has(permissionType)) {
                processedParentTypes.add(permissionType);

                const parentPermissionPayload = this.createPermissionPayload(permissionType, accessType, currentFormValues);

                if (parentPermissionPayload) {
                    changedPermissions.push(parentPermissionPayload);
                }
            } else if (!isParentPermission) {
                const parentType = this.getParentTypeForDerivedPermission(permissionType);

                if (parentType && !processedParentTypes.has(parentType)) {
                    processedParentTypes.add(parentType);

                    const parentAccessType = this.getParentAccessType(parentType, currentFormValues);
                    if (parentAccessType) {
                        const parentPermissionPayload = this.createPermissionPayload(parentType, parentAccessType, currentFormValues);

                        if (parentPermissionPayload) {
                            changedPermissions.push(parentPermissionPayload);
                        }
                    }
                }
            }
        });

        return changedPermissions;
    }

    private isParentPermission(permissionType: VisualPermissionsEnum): boolean {
        return [
            VisualPermissionsEnum.ASSETS_METADATA,
            VisualPermissionsEnum.ROOM_TYPES,
            VisualPermissionsEnum.MEETING_ROOM_TYPES,
            VisualPermissionsEnum.PHOTO_ORGANIZER,
            VisualPermissionsEnum.CUSTOM_CONTENT,
        ].includes(permissionType);
    }

    private getParentTypeForDerivedPermission(derivedType: VisualPermissionsEnum): VisualPermissionsEnum | null {
        const derivedToParentMap: Record<string, VisualPermissionsEnum> = {
            [VisualPermissionsEnum.CREATE_ASSETS]: VisualPermissionsEnum.ASSETS_METADATA,
            [VisualPermissionsEnum.DELETE_ASSETS]: VisualPermissionsEnum.ASSETS_METADATA,
            [VisualPermissionsEnum.ENABLE_DISABLE_ASSETS]: VisualPermissionsEnum.ASSETS_METADATA,
            [VisualPermissionsEnum.REORDER_ASSETS]: VisualPermissionsEnum.ASSETS_METADATA,
            [VisualPermissionsEnum.CROP_ASSETS]: VisualPermissionsEnum.ASSETS_METADATA,
            [VisualPermissionsEnum.DOWNLOAD_ASSETS]: VisualPermissionsEnum.ASSETS_METADATA,
        };

        return derivedToParentMap[derivedType] || null;
    }

    private getParentAccessType(
        parentType: VisualPermissionsEnum,
        currentFormValues: Record<string, boolean>,
    ): VisualPermissionsAccessTypeEnum | null {
        if (currentFormValues[`${parentType}_${VisualPermissionsAccessTypeEnum.EDIT}`]) {
            return VisualPermissionsAccessTypeEnum.EDIT;
        }
        if (currentFormValues[`${parentType}_${VisualPermissionsAccessTypeEnum.VIEW}`]) {
            return VisualPermissionsAccessTypeEnum.VIEW;
        }
        return null;
    }

    private createPermissionPayload(
        permissionType: VisualPermissionsEnum,
        accessType: VisualPermissionsAccessTypeEnum,
        currentFormValues: Record<string, boolean>,
    ): BackendPermissionPayload | null {
        const viewChecked = currentFormValues[`${permissionType}_${VisualPermissionsAccessTypeEnum.VIEW}`] || false;
        const editChecked = currentFormValues[`${permissionType}_${VisualPermissionsAccessTypeEnum.EDIT}`] || false;

        let finalAccessType: VisualPermissionsAccessTypeEnum;

        if (editChecked) {
            finalAccessType = VisualPermissionsAccessTypeEnum.EDIT;
        } else if (viewChecked) {
            finalAccessType = VisualPermissionsAccessTypeEnum.VIEW;
        } else {
            finalAccessType = VisualPermissionsAccessTypeEnum.HIDE;
        }

        const originalFormState = this.originalFormStateSignal();
        const derivedPermissions = this.getDerivedPermissionsForParent(permissionType, currentFormValues, originalFormState);

        return {
            type: permissionType,
            accessType: finalAccessType,
            ...(derivedPermissions.length > 0 && { derivedVisualPermissions: derivedPermissions }),
        };
    }

    private getDerivedPermissionsForParent(
        parentType: VisualPermissionsEnum,
        currentFormValues: Record<string, boolean>,
        originalFormState: Record<string, boolean>,
    ): { type: VisualPermissionsEnum; isActive: boolean }[] {
        const derivedPermissions: { type: VisualPermissionsEnum; isActive: boolean }[] = [];

        if (parentType === VisualPermissionsEnum.ASSETS_METADATA) {
            const derivedTypes = [
                VisualPermissionsEnum.CREATE_ASSETS,
                VisualPermissionsEnum.DELETE_ASSETS,
                VisualPermissionsEnum.ENABLE_DISABLE_ASSETS,
                VisualPermissionsEnum.REORDER_ASSETS,
                VisualPermissionsEnum.CROP_ASSETS,
                VisualPermissionsEnum.DOWNLOAD_ASSETS,
            ];

            derivedTypes.forEach((derivedType) => {
                const controlName = `${derivedType}_${VisualPermissionsAccessTypeEnum.ACTIVE}`;
                if (controlName in currentFormValues) {
                    const currentValue = currentFormValues[controlName] || false;
                    const originalValue = originalFormState[controlName] || false;

                    if (currentValue !== originalValue) {
                        derivedPermissions.push({
                            type: derivedType,
                            isActive: currentValue,
                        });
                    }
                }
            });
        }

        return derivedPermissions;
    }

    private setupFormValidation(): void {
        this.permissionsForm.valueChanges.subscribe((formValues) => {
            if (!this.formInitialized()) {
                return;
            }

            const booleanFormValues: Record<string, boolean> = {};
            Object.entries(formValues || {}).forEach(([key, value]) => {
                booleanFormValues[key] = Boolean(value);
            });

            this.handleParentChildValidation(booleanFormValues);
            this.handleHierarchicalValidation(booleanFormValues);
            this.publishNotAllowedChanges();
        });
    }

    /**
     * Publishes the visual permission types the acting user changed but is not allowed to set, so the
     * permissions control can block Save before the request leaves the browser.
     */
    private publishNotAllowedChanges(): void {
        this.visualsPermissionsService.notAllowedVisualPermissionChanges.set(this.getNotAllowedChanges());
    }

    private getNotAllowedChanges(): VisualPermissionsEnum[] {
        const actingUserAccessTypes = this.getActingUserVisualAccessTypes();

        if (!actingUserAccessTypes || !this.formInitialized()) {
            return [];
        }

        const originalFormValues = this.originalFormStateSignal();
        const currentFormValues: Record<string, boolean> = {};
        Object.entries(this.permissionsForm.value || {}).forEach(([key, value]) => {
            currentFormValues[key] = Boolean(value);
        });

        const notAllowedTypes: VisualPermissionsEnum[] = [];

        this.permissionsSetup()
            .filter((permissionSetup) => permissionSetup.isImplemented)
            .forEach((permissionSetup) => {
                const type = permissionSetup.name;
                const storedAccessType = this.getAccessTypeFromFormValues(type, originalFormValues);
                const currentAccessType = this.getAccessTypeFromFormValues(type, currentFormValues);
                const actingUserRank = this.getVisualAccessTypeRank(actingUserAccessTypes[type]);

                const changedDerivedPermissions = (permissionSetup.children ?? []).filter((derivedPermission) => {
                    const controlName = `${derivedPermission.name}_${VisualPermissionsAccessTypeEnum.ACTIVE}`;
                    return Boolean(currentFormValues[controlName]) !== Boolean(originalFormValues[controlName]);
                });

                const hasChanges = storedAccessType !== currentAccessType || changedDerivedPermissions.length > 0;

                if (!hasChanges) {
                    return;
                }

                const requiredRank = Math.max(
                    this.getVisualAccessTypeRank(storedAccessType),
                    this.getVisualAccessTypeRank(currentAccessType),
                );

                const activatesDerivedPermission = changedDerivedPermissions.some(
                    (derivedPermission) => currentFormValues[`${derivedPermission.name}_${VisualPermissionsAccessTypeEnum.ACTIVE}`],
                );

                if (requiredRank > actingUserRank || (activatesDerivedPermission && actingUserRank === 0)) {
                    notAllowedTypes.push(type);
                }
            });

        return notAllowedTypes;
    }

    /**
     * The acting user's own access type per visual permission type. Returns null for Shiji and system users,
     * who are not capped by their own permissions.
     */
    private getActingUserVisualAccessTypes(): { [type: string]: VisualPermissionsAccessTypeEnum } | null {
        const currentUser = this.userApiService.currentUser;

        if (!currentUser || !isCurrentSupplierUser(currentUser)) {
            return null;
        }

        const accessTypes: { [type: string]: VisualPermissionsAccessTypeEnum } = {};

        (currentUser.visualPermissions ?? []).forEach((permission) => {
            accessTypes[permission.type] = permission.accessType;
        });

        return accessTypes;
    }

    private getAccessTypeFromFormValues(type: VisualPermissionsEnum, formValues: Record<string, boolean>): VisualPermissionsAccessTypeEnum {
        if (formValues[`${type}_${VisualPermissionsAccessTypeEnum.EDIT}`]) {
            return VisualPermissionsAccessTypeEnum.EDIT;
        }

        if (formValues[`${type}_${VisualPermissionsAccessTypeEnum.VIEW}`]) {
            return VisualPermissionsAccessTypeEnum.VIEW;
        }

        return VisualPermissionsAccessTypeEnum.HIDE;
    }

    private getVisualAccessTypeRank(accessType?: VisualPermissionsAccessTypeEnum): number {
        if (accessType === VisualPermissionsAccessTypeEnum.EDIT) {
            return 2;
        }

        if (accessType === VisualPermissionsAccessTypeEnum.VIEW) {
            return 1;
        }

        return 0;
    }

    private handleHierarchicalValidation(formValues: Record<string, boolean>): void {
        const permissionsList = this.permissionsSetup();

        permissionsList.forEach((permission) => {
            this.applyHierarchicalLogic(permission, formValues);
        });
    }

    private applyHierarchicalLogic(permission: VisualPermission, formValues: Record<string, boolean>): void {
        const sortedAccessTypes = permission.possibleAccessTypes
            .slice()
            .sort((a, b) => this.accessTypeHierarchy.indexOf(a) - this.accessTypeHierarchy.indexOf(b));

        sortedAccessTypes.forEach((accessType, index) => {
            const controlName = `${permission.name}_${accessType}`;
            const control = this.permissionsForm.get(controlName);
            const isChecked = formValues[controlName];

            if (!control) return;

            if (isChecked) {
                for (let accessTypeIndex = 0; accessTypeIndex < index; accessTypeIndex++) {
                    const lowerAccessType = sortedAccessTypes[accessTypeIndex];
                    const lowerControlName = `${permission.name}_${lowerAccessType}`;
                    const lowerControl = this.permissionsForm.get(lowerControlName);

                    if (lowerControl && !lowerControl.value) {
                        lowerControl.setValue(true, { emitEvent: false });
                    }
                    if (lowerControl) {
                        lowerControl.disable({ emitEvent: false });
                    }
                }
            } else {
                for (let accessTypeIndex = 0; accessTypeIndex < index; accessTypeIndex++) {
                    const lowerAccessType = sortedAccessTypes[accessTypeIndex];
                    const lowerControlName = `${permission.name}_${lowerAccessType}`;
                    const lowerControl = this.permissionsForm.get(lowerControlName);

                    if (lowerControl) {
                        const shouldStillBeDisabled = sortedAccessTypes.slice(accessTypeIndex + 1).some((higherType) => {
                            const higherControlName = `${permission.name}_${higherType}`;
                            const higherControl = this.permissionsForm.get(higherControlName);
                            return higherControl?.value;
                        });

                        if (!shouldStillBeDisabled) {
                            lowerControl.enable({ emitEvent: false });
                        }
                    }
                }
            }
        });

        permission.children?.forEach((child) => {
            this.applyHierarchicalLogic(child, formValues);
        });
    }

    private handleParentChildValidation(formValues: Record<string, boolean>): void {
        const permissionsList = this.permissionsSetup();

        permissionsList.forEach((permission) => {
            this.validatePermissionAndChildren(permission, formValues);
        });
    }

    private validatePermissionAndChildren(permission: VisualPermission, formValues: Record<string, boolean>): void {
        if (permission.children && permission.children.length > 0) {
            permission.possibleAccessTypes.forEach((accessType) => {
                const parentControlName = `${permission.name}_${accessType}`;
                const parentValue = formValues[parentControlName];

                if (!parentValue && permission.children) {
                    this.uncheckDependentChildren(permission.children, accessType);
                }
            });
        }

        permission.children?.forEach((child) => {
            this.validatePermissionAndChildren(child, formValues);
        });
    }

    private uncheckDependentChildren(children: VisualPermission[], parentAccessType: VisualPermissionsAccessTypeEnum): void {
        children.forEach((child) => {
            if (child.dependsOnAccessType === parentAccessType) {
                child.possibleAccessTypes.forEach((childAccessType) => {
                    const childControlName = `${child.name}_${childAccessType}`;
                    const childControl = this.permissionsForm.get(childControlName);

                    if (childControl && childControl.value) {
                        childControl.setValue(false, { emitEvent: false });
                    }
                });

                if (child.children && child.children.length > 0) {
                    child.possibleAccessTypes.forEach((childAccessType) => {
                        this.uncheckDependentChildren(child.children!, childAccessType);
                    });
                }
            }
        });
    }

    private processPermission(
        permission: VisualPermission,
        formValues: Record<string, boolean>,
        implementedMap: Map<VisualPermissionsEnum, boolean>,
    ): ProcessedPermission {
        const isImplemented = implementedMap.get(permission.name) ?? false;

        const processedChildren =
            permission.children
                ?.map((child) => this.processPermission(child, formValues, implementedMap))
                .filter((child) => child.isImplemented) || [];

        return {
            ...permission,
            isVisible: this.isPermissionVisible(permission, formValues),
            isImplemented,
            translationKey: 'users.supplierUsers.edit.visualPermissions.names.' + permission.name.toLowerCase(),
            children: processedChildren,
        };
    }

    private isPermissionVisible(permission: VisualPermission, formValues: Record<string, boolean>): boolean {
        if (!permission.dependsOnAccessType) {
            return true;
        }

        const parentPermission = this.findParentPermission(permission);
        if (!parentPermission) {
            return true;
        }

        const dependencyKey = `${parentPermission.name}_${permission.dependsOnAccessType}`;
        return formValues[dependencyKey] === true;
    }

    private findParentPermission(targetPermission: VisualPermission): VisualPermission | null {
        const findInPermissions = (permissionsList: VisualPermission[]): VisualPermission | null => {
            for (const permission of permissionsList) {
                if (permission.children?.includes(targetPermission)) {
                    return permission;
                }
                if (permission.children) {
                    const foundInChildren = findInPermissions(permission.children);
                    if (foundInChildren) {
                        return foundInChildren;
                    }
                }
            }
            return null;
        };

        return findInPermissions(this.permissionsSetup());
    }

    private generatePermissionsForm(permissionsList: VisualPermission[]): void {
        permissionsList.forEach((permission) => {
            permission.possibleAccessTypes.forEach((accessType) => {
                const controlName = `${permission.name}_${accessType}`;
                if (!this.permissionsForm.get(controlName)) {
                    this.permissionsForm.addControl(controlName, new FormControl<boolean>(false, { nonNullable: true }));
                }
            });
            if (permission.children) {
                this.generatePermissionsForm(permission.children);
            }
        });
    }

    originalOrder = (): number => 0;

    ngOnDestroy(): void {
        this.visualsPermissionsService.loadDefaultPermissions.set(false);
        this.visualsPermissionsService.userId.set(null);
        this.visualsPermissionsService.userGroupId.set(null);
        this.visualsPermissionsService.visualsSectionActive.set(false);
        this.visualsPermissionsService.notAllowedVisualPermissionChanges.set([]);
    }
}
